import { readAnalyticsConsent } from './consent.js';

const CMP_ROOT_SELECTOR = '#cookie_consent_banner_main_app';
const CMP_WRAPPER_SELECTOR = '.cookie_consent-banner-wrapper';
const CMP_HIDDEN_CLASS = 'cookie_consent-banner-wrapper--hidden';
const CMP_SETTINGS_SELECTOR = '.cookie_consent-privacy_setting_banner';
const CHAT_HOST_SELECTOR = '[data-pickles-chat-host]';
const CMP_DISCOVERY_TIMEOUT_MS = 12000;
const CHAT_HOST_DISCOVERY_TIMEOUT_MS = 20000;
const CONSENT_RETRY_LIMIT = 12;
const PICKLES_SDK_HOST = 'pickles.internal.spice-factory.co.jp';
const experienceCleanups = new WeakMap();
const settleTimers = new WeakMap();
// These buffers intentionally exceed the corresponding CSS animation lengths
// in elements/pickles-chat-template.php so the settled state never cuts off a
// frame that is still being composited.
const LAUNCHER_SETTLE_DELAY_MS = 1350;
const PANEL_SETTLE_DELAY_MS = 950;
const RESTORE_SETTLE_DELAY_MS = 720;
const FIXED_BOTTOM_UI_SELECTORS = Object.freeze([
  '.p-faq__summary-btn',
  '.p-policy-contents-trigger',
  '.p-magazine-filter__trigger',
  '.p-magazine__summary-trigger',
  '.p-tags-archive-filter__sp-trigger',
  '.p-service-archive__sp-nav',
  '.p-recruit-banner',
]);

export const CORPORATE_CHAT_PROMPTS = Object.freeze([
  'サイトについて質問してください',
  'AI開発について相談できますか？',
  '物流業界での実績はありますか？',
]);

function clearSettleTimers(host, windowObject) {
  const hostTimers = settleTimers.get(host);
  hostTimers?.forEach(timer => windowObject.clearTimeout(timer));
  settleTimers.delete(host);
}

function settleAfter(
  host,
  attribute,
  delay,
  windowObject,
  predicate = () => true
) {
  let hostTimers = settleTimers.get(host);
  if (!hostTimers) {
    hostTimers = new Map();
    settleTimers.set(host, hostTimers);
  }

  const previousTimer = hostTimers.get(attribute);
  if (previousTimer !== undefined) windowObject.clearTimeout(previousTimer);
  host.removeAttribute(attribute);
  const timer = windowObject.setTimeout(() => {
    hostTimers.delete(attribute);
    if (host.isConnected !== false && predicate())
      host.setAttribute(attribute, 'true');
  }, delay);
  hostTimers.set(attribute, timer);
}

function cancelSettle(host, attribute, windowObject) {
  const hostTimers = settleTimers.get(host);
  const timer = hostTimers?.get(attribute);
  if (timer !== undefined) {
    windowObject.clearTimeout(timer);
    hostTimers.delete(attribute);
  }
  host.removeAttribute(attribute);
}

function launcherCanAnimate(host, input, root, windowObject) {
  return (
    host.getAttribute('data-pc-open') !== 'true' &&
    host.getAttribute('data-pc-dismissed') !== 'true' &&
    host.getAttribute('data-spice-cmp-suppressed') !== 'true' &&
    root.activeElement !== input &&
    !input.value &&
    windowObject.document?.visibilityState !== 'hidden'
  );
}

function createTypewriterLifecycle(input, root, host, windowObject) {
  let stopped = false;
  let wakeResolver = null;
  let delayTimer = null;
  let delayResolver = null;

  const wake = () => {
    if (!stopped) {
      wakeResolver?.();
      wakeResolver = null;
    }
  };

  return {
    get stopped() {
      return stopped;
    },
    wake,
    waitUntilAnimatable() {
      if (stopped || launcherCanAnimate(host, input, root, windowObject)) {
        return Promise.resolve();
      }
      return new Promise(resolve => {
        wakeResolver = resolve;
      });
    },
    delay(milliseconds) {
      if (stopped) return Promise.resolve();
      return new Promise(resolve => {
        delayResolver = resolve;
        delayTimer = windowObject.setTimeout(() => {
          delayTimer = null;
          delayResolver = null;
          resolve();
        }, milliseconds);
      });
    },
    stop() {
      stopped = true;
      wakeResolver?.();
      wakeResolver = null;
      if (delayTimer !== null) windowObject.clearTimeout(delayTimer);
      delayTimer = null;
      delayResolver?.();
      delayResolver = null;
    },
  };
}

async function runPromptTypewriter(input, root, host, windowObject, lifecycle) {
  input.placeholder = CORPORATE_CHAT_PROMPTS[0];
  if (windowObject.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    return;
  }

  let promptIndex = 0;
  while (input.isConnected !== false && !lifecycle.stopped) {
    await lifecycle.waitUntilAnimatable();
    if (lifecycle.stopped || input.isConnected === false) return;
    const prompt =
      CORPORATE_CHAT_PROMPTS[promptIndex % CORPORATE_CHAT_PROMPTS.length];
    input.placeholder = '';
    for (const character of prompt) {
      if (input.isConnected === false) return;
      if (!launcherCanAnimate(host, input, root, windowObject)) break;
      input.placeholder += character;
      await lifecycle.delay(58);
      if (lifecycle.stopped) return;
    }
    if (!launcherCanAnimate(host, input, root, windowObject)) continue;
    await lifecycle.delay(2400);
    if (lifecycle.stopped) return;
    if (launcherCanAnimate(host, input, root, windowObject)) {
      while (input.placeholder.length > 0) {
        if (!launcherCanAnimate(host, input, root, windowObject)) break;
        input.placeholder = input.placeholder.slice(0, -1);
        await lifecycle.delay(28);
        if (lifecycle.stopped) return;
      }
    }
    promptIndex += 1;
    await lifecycle.delay(260);
  }
}

function setupFixedBottomCollisionAvoidance(
  host,
  documentObject,
  windowObject
) {
  if (
    !documentObject?.querySelectorAll ||
    !windowObject?.getComputedStyle ||
    !host?.style?.setProperty
  ) {
    return () => {};
  }

  let elements = [];
  const elementObservers = new Map();
  let bodyObserver = null;
  let frame = null;

  const update = () => {
    frame = null;
    const viewportWidth =
      windowObject.innerWidth ||
      documentObject.documentElement?.clientWidth ||
      0;
    const viewportHeight =
      windowObject.innerHeight ||
      documentObject.documentElement?.clientHeight ||
      0;
    if (!viewportWidth || !viewportHeight) return;

    const mobile = viewportWidth <= 768;
    const baseBottom = mobile ? 12 : 24;
    const launcherWidth = mobile
      ? viewportWidth - 24
      : Math.min(700, viewportWidth - 32);
    const launcherLeft = (viewportWidth - launcherWidth) / 2;
    const launcherRight = launcherLeft + launcherWidth;
    const restoreRight = viewportWidth - (mobile ? 12 : 24);
    const restoreLeft = restoreRight - 56;
    let launcherBottom = baseBottom;
    let restoreBottom = baseBottom;

    elements.forEach(element => {
      if (typeof element.getBoundingClientRect !== 'function') return;
      const style = windowObject.getComputedStyle(element);
      if (
        style.position !== 'fixed' ||
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        style.pointerEvents === 'none' ||
        Number(style.opacity) === 0
      ) {
        return;
      }
      const rect = element.getBoundingClientRect();
      if (
        rect.width <= 0 ||
        rect.height <= 0 ||
        rect.bottom <= 0 ||
        rect.top >= viewportHeight
      ) {
        return;
      }
      const candidateBottom = Math.ceil(viewportHeight - rect.top + 12);
      if (rect.right > launcherLeft && rect.left < launcherRight) {
        launcherBottom = Math.max(launcherBottom, candidateBottom);
      }
      if (rect.right > restoreLeft && rect.left < restoreRight) {
        restoreBottom = Math.max(restoreBottom, candidateBottom);
      }
    });

    host.style.setProperty('--pc-launcher-bottom', `${launcherBottom}px`);
    host.style.setProperty('--pc-restore-bottom', `${restoreBottom}px`);
  };
  const scheduleUpdate = () => {
    if (frame !== null) return;
    frame = windowObject.requestAnimationFrame(update);
  };
  const refreshElements = () => {
    const nextElements = Array.from(
      documentObject.querySelectorAll(FIXED_BOTTOM_UI_SELECTORS.join(','))
    );
    elementObservers.forEach((observer, element) => {
      if (!nextElements.includes(element)) {
        observer.disconnect();
        elementObservers.delete(element);
      }
    });
    nextElements.forEach(element => {
      if (elementObservers.has(element)) return;
      const observer = new windowObject.MutationObserver(scheduleUpdate);
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class', 'hidden', 'style', 'aria-expanded'],
      });
      elementObservers.set(element, observer);
    });
    elements = nextElements;
    scheduleUpdate();
  };
  if (documentObject.body) {
    bodyObserver = new windowObject.MutationObserver(refreshElements);
    bodyObserver.observe(documentObject.body, {
      childList: true,
      subtree: true,
    });
  }
  windowObject.addEventListener?.('resize', scheduleUpdate);
  windowObject.addEventListener?.('orientationchange', scheduleUpdate);
  refreshElements();

  return () => {
    bodyObserver?.disconnect();
    elementObservers.forEach(observer => observer.disconnect());
    elementObservers.clear();
    windowObject.removeEventListener?.('resize', scheduleUpdate);
    windowObject.removeEventListener?.('orientationchange', scheduleUpdate);
    if (frame !== null) windowObject.cancelAnimationFrame(frame);
  };
}

export function initializeCorporateChatExperience(host, windowObject) {
  const root = host.shadowRoot;
  const input = root?.querySelector?.('[data-pc="launcher-input"]');
  if (!input) return false;
  if (experienceCleanups.has(host)) return true;

  const cleanupCallbacks = [];
  const lifecycle = createTypewriterLifecycle(input, root, host, windowObject);
  const wakeTypewriter = () => lifecycle.wake();

  settleAfter(
    host,
    'data-pc-motion-settled',
    LAUNCHER_SETTLE_DELAY_MS,
    windowObject
  );

  const syncMotionState = () => {
    const isOpen = host.getAttribute('data-pc-open') === 'true';
    const isDismissed = host.getAttribute('data-pc-dismissed') === 'true';

    if (isOpen) {
      settleAfter(
        host,
        'data-pc-panel-settled',
        PANEL_SETTLE_DELAY_MS,
        windowObject,
        () => host.getAttribute('data-pc-open') === 'true'
      );
    } else {
      cancelSettle(host, 'data-pc-panel-settled', windowObject);
    }

    if (isDismissed) {
      settleAfter(
        host,
        'data-pc-restore-settled',
        RESTORE_SETTLE_DELAY_MS,
        windowObject,
        () => host.getAttribute('data-pc-dismissed') === 'true'
      );
    } else {
      cancelSettle(host, 'data-pc-restore-settled', windowObject);
    }
    wakeTypewriter();
  };
  const motionObserver = new windowObject.MutationObserver(syncMotionState);
  motionObserver.observe(host, {
    attributes: true,
    attributeFilter: [
      'data-pc-open',
      'data-pc-dismissed',
      'data-spice-cmp-suppressed',
    ],
  });

  const restore = root.querySelector?.('[data-pc="restore"]');
  const handleRestore = () => {
    settleAfter(
      host,
      'data-pc-motion-settled',
      LAUNCHER_SETTLE_DELAY_MS,
      windowObject
    );
  };
  restore?.addEventListener?.('click', handleRestore);

  // Defense in depth: even if a future SDK version misses either submit
  // handler, never allow native form navigation to reload the page.
  const launcherForm = root.querySelector?.('[data-pc="launcher-form"]');
  const chatForm = root.querySelector?.('[data-pc="form"]');
  const preventNativeSubmit = event => event.preventDefault();
  launcherForm?.addEventListener?.('submit', preventNativeSubmit);
  chatForm?.addEventListener?.('submit', preventNativeSubmit);

  input.addEventListener?.('focus', wakeTypewriter);
  input.addEventListener?.('blur', wakeTypewriter);
  input.addEventListener?.('input', wakeTypewriter);
  windowObject.document?.addEventListener?.('visibilitychange', wakeTypewriter);
  cleanupCallbacks.push(
    setupFixedBottomCollisionAvoidance(
      host,
      windowObject.document,
      windowObject
    )
  );

  // The SDK may restore dismissed state from localStorage before this loader
  // discovers the host, so settle the already-restored state immediately.
  syncMotionState();
  void runPromptTypewriter(input, root, host, windowObject, lifecycle);
  experienceCleanups.set(host, () => {
    lifecycle.stop();
    motionObserver.disconnect();
    clearSettleTimers(host, windowObject);
    restore?.removeEventListener?.('click', handleRestore);
    launcherForm?.removeEventListener?.('submit', preventNativeSubmit);
    chatForm?.removeEventListener?.('submit', preventNativeSubmit);
    input.removeEventListener?.('focus', wakeTypewriter);
    input.removeEventListener?.('blur', wakeTypewriter);
    input.removeEventListener?.('input', wakeTypewriter);
    windowObject.document?.removeEventListener?.(
      'visibilitychange',
      wakeTypewriter
    );
    cleanupCallbacks.forEach(cleanup => cleanup());
  });
  return true;
}

export function teardownCorporateChatExperience(host) {
  experienceCleanups.get(host)?.();
  experienceCleanups.delete(host);
}

export function createPicklesChatStateMachine({
  injectSdk,
  teardownSdk,
  setChatSuppressed,
  scheduleIdle,
  cancelIdle,
}) {
  let state = 'waiting';
  let cmpBlocked = true;
  let idleHandle = null;

  const update = blocked => {
    if (state === 'revoked') return;
    cmpBlocked = blocked;
    if (state === 'injected') {
      setChatSuppressed(blocked);
      return;
    }
    if (blocked) {
      if (state === 'scheduled' && idleHandle !== null) {
        cancelIdle(idleHandle);
        idleHandle = null;
      }
      state = 'blocked';
      return;
    }
    if (state === 'scheduled') {
      return;
    }
    state = 'scheduled';
    idleHandle = scheduleIdle(() => {
      idleHandle = null;
      if (cmpBlocked || state !== 'scheduled') {
        state = 'blocked';
        return;
      }
      // Mark injected before appending the script so synchronous CMP events
      // cannot schedule a second SDK element.
      state = 'injected';
      injectSdk();
      setChatSuppressed(false);
    });
  };

  return {
    getState: () => state,
    revoke() {
      if (state === 'revoked') return;
      if (state === 'scheduled' && idleHandle !== null) {
        cancelIdle(idleHandle);
        idleHandle = null;
      }
      if (state === 'injected') teardownSdk();
      state = 'revoked';
      cmpBlocked = true;
    },
    cancel() {
      if (state === 'scheduled' && idleHandle !== null) {
        cancelIdle(idleHandle);
        idleHandle = null;
      }
      state = 'blocked';
      cmpBlocked = true;
    },
    update,
  };
}

function elementIsVisible(element, windowObject) {
  if (
    !element ||
    element.hidden ||
    element.getAttribute('aria-hidden') === 'true' ||
    element.classList.contains(CMP_HIDDEN_CLASS)
  ) {
    return false;
  }
  const style = windowObject.getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden';
}

function cmpIsBlocking(root, windowObject) {
  const wrappers = Array.from(root.querySelectorAll(CMP_WRAPPER_SELECTOR));
  const orphanSettings = Array.from(
    root.querySelectorAll(CMP_SETTINGS_SELECTOR)
  ).filter(element => !element.closest(CMP_WRAPPER_SELECTOR));
  return [...wrappers, ...orphanSettings].some(element =>
    elementIsVisible(element, windowObject)
  );
}

function scheduleWhenIdle(windowObject, callback) {
  if (typeof windowObject.requestIdleCallback === 'function') {
    return windowObject.requestIdleCallback(callback, { timeout: 2500 });
  }
  return windowObject.setTimeout(callback, 250);
}

function cancelScheduledIdle(windowObject, handle) {
  if (typeof windowObject.cancelIdleCallback === 'function') {
    windowObject.cancelIdleCallback(handle);
    return;
  }
  windowObject.clearTimeout(handle);
}

function validConfig(element) {
  // Defense in depth: the same public configuration contract is validated by
  // the deployment shell and PHP before this DOM-level tamper check runs.
  if (!element) return null;
  const chatKey = element.dataset.chatKey || '';
  const templateId = element.dataset.templateId || '';
  if (!/^pc_[A-Za-z0-9_-]{8,100}$/.test(chatKey) || !templateId) return null;
  try {
    const sdkUrl = new URL(element.dataset.sdkUrl || '');
    if (
      sdkUrl.protocol !== 'https:' ||
      sdkUrl.username ||
      sdkUrl.password ||
      sdkUrl.hostname !== PICKLES_SDK_HOST ||
      sdkUrl.port ||
      sdkUrl.pathname !== '/api/public/site-chat/embed.js' ||
      sdkUrl.hash
    ) {
      return null;
    }
    const query = [...sdkUrl.searchParams.entries()];
    if (
      query.length > 1 ||
      (query.length === 1 &&
        (query[0][0] !== 'v' || !/^[A-Za-z0-9._-]{1,40}$/.test(query[0][1])))
    )
      return null;
    return { chatKey, sdkUrl: sdkUrl.href, templateId };
  } catch {
    return null;
  }
}

export function startPicklesChatLoader(
  documentObject = document,
  windowObject = window
) {
  const config = validConfig(
    documentObject.getElementById('js-pickles-chat-bootstrap')
  );
  if (!config) return null;

  let cmpRoot = null;
  let rootObserver = null;
  let rootDiscoveryObserver = null;
  let discoveryTimeout = null;
  let hostDiscoveryObserver = null;
  let hostDiscoveryTimeout = null;
  let cmpEvaluationFrame = null;
  let consentRetryTimeout = null;
  let currentCmpBlocked = true;
  let cmpWrapperObserved = false;
  let cmpDiscoveryExpired = false;
  let chatHostObserved = false;
  let chatHosts = [];
  let revocationReloadRequested = false;
  let consentRetryAttempts = 0;

  const stopWatchingForChatHost = () => {
    hostDiscoveryObserver?.disconnect();
    hostDiscoveryObserver = null;
    if (hostDiscoveryTimeout !== null) {
      windowObject.clearTimeout(hostDiscoveryTimeout);
      hostDiscoveryTimeout = null;
    }
  };

  const discoverChatHosts = () => {
    const discoveredHosts = Array.from(
      documentObject.querySelectorAll(CHAT_HOST_SELECTOR)
    );
    if (discoveredHosts.length > 0) chatHosts = discoveredHosts;
  };

  const applyChatSuppression = suppressed => {
    chatHosts.forEach(host => {
      if (suppressed) {
        if (
          documentObject.activeElement === host &&
          typeof host.blur === 'function'
        )
          host.blur();
        host.hidden = true;
        host.inert = true;
        host.setAttribute('aria-hidden', 'true');
        host.setAttribute('data-spice-cmp-suppressed', 'true');
      } else if (host.getAttribute('data-spice-cmp-suppressed') === 'true') {
        host.hidden = false;
        host.inert = false;
        host.removeAttribute('aria-hidden');
        host.removeAttribute('data-spice-cmp-suppressed');
      }
    });
  };

  const initializeChatHosts = () => {
    const allHostsReady =
      chatHosts.length > 0 &&
      chatHosts.every(host =>
        initializeCorporateChatExperience(host, windowObject)
      );
    if (allHostsReady) {
      chatHostObserved = true;
      stopWatchingForChatHost();
    }
  };

  const syncChatHosts = suppressed => {
    currentCmpBlocked = suppressed;
    discoverChatHosts();
    applyChatSuppression(suppressed);
    initializeChatHosts();
  };

  const watchForChatHost = () => {
    syncChatHosts(currentCmpBlocked);
    if (chatHostObserved || hostDiscoveryObserver) return;
    hostDiscoveryObserver = new windowObject.MutationObserver(() => {
      syncChatHosts(currentCmpBlocked);
    });
    hostDiscoveryObserver.observe(documentObject.body, {
      childList: true,
      subtree: true,
    });
    hostDiscoveryTimeout = windowObject.setTimeout(() => {
      if (!chatHostObserved) {
        console.warn(
          chatHosts.length > 0
            ? 'Pickles chat host mounted without the corporate launcher template.'
            : 'Pickles chat host was not mounted by the SDK.'
        );
      }
      stopWatchingForChatHost();
    }, CHAT_HOST_DISCOVERY_TIMEOUT_MS);
  };

  const teardownSdk = () => {
    currentCmpBlocked = true;
    discoverChatHosts();
    applyChatSuppression(true);
    stopWatchingForChatHost();
    documentObject
      .querySelector('script[data-spice-pickles-chat-sdk]')
      ?.remove?.();
    chatHosts.forEach(host => {
      teardownCorporateChatExperience(host);
      host.remove?.();
    });
  };

  const reloadAfterRevocation = () => {
    if (revocationReloadRequested) return;
    revocationReloadRequested = true;
    // The public SDK has no teardown API. Reload after the CMP finishes its
    // save/close transition so SDK listeners, timers and Turnstile are gone;
    // the persisted rejected state then keeps the next load fail-closed.
    windowObject.location?.reload?.();
  };

  const injectSdk = () => {
    if (documentObject.querySelector('script[data-spice-pickles-chat-sdk]'))
      return;
    const script = documentObject.createElement('script');
    script.src = config.sdkUrl;
    script.async = true;
    script.defer = true;
    script.setAttribute('data-pickles-chat-key', config.chatKey);
    script.setAttribute('data-pickles-chat-template', config.templateId);
    script.setAttribute('data-spice-pickles-chat-sdk', '1');
    // Injection is reached only after STRIGHT ONE reports analytics consent.
    // Ignore its second script blocker to avoid blocking an already-authorized
    // dynamic script between the consent transition and DOM insertion.
    script.setAttribute('data-sto-ignore', 'true');
    documentObject.head.appendChild(script);
    watchForChatHost();
  };

  const machine = createPicklesChatStateMachine({
    injectSdk,
    teardownSdk,
    setChatSuppressed: syncChatHosts,
    scheduleIdle: callback => scheduleWhenIdle(windowObject, callback),
    cancelIdle: handle => cancelScheduledIdle(windowObject, handle),
  });

  const evaluateCmp = () => {
    if (!cmpRoot) return;
    const consent = readAnalyticsConsent({
      cookie: documentObject.cookie || '',
      storage: windowObject.localStorage,
    });
    const wrapper = cmpRoot.querySelector(CMP_WRAPPER_SELECTOR);
    // A wrapper grace period is needed only while consent is unknown. A
    // returning visitor with a persisted decision should not wait 12 seconds.
    if (
      consent === null &&
      !wrapper &&
      !cmpWrapperObserved &&
      !cmpDiscoveryExpired
    )
      return;
    if (wrapper) {
      cmpWrapperObserved = true;
      if (discoveryTimeout !== null) {
        windowObject.clearTimeout(discoveryTimeout);
        discoveryTimeout = null;
      }
    }
    const cmpBlocking = cmpIsBlocking(cmpRoot, windowObject);
    if (consent === false) {
      if (machine.getState() === 'injected') machine.revoke();
      if (machine.getState() === 'revoked' && !cmpBlocking)
        reloadAfterRevocation();
    }
    if (machine.getState() === 'revoked') {
      return;
    }
    machine.update(cmpBlocking || consent !== true);
    if (cmpBlocking) consentRetryAttempts = 0;
    if (
      !cmpBlocking &&
      consent === null &&
      consentRetryAttempts < CONSENT_RETRY_LIMIT &&
      consentRetryTimeout === null
    ) {
      consentRetryAttempts += 1;
      consentRetryTimeout = windowObject.setTimeout(() => {
        consentRetryTimeout = null;
        evaluateCmp();
      }, 250);
    }
  };

  const queueCmpEvaluation = () => {
    if (cmpEvaluationFrame !== null) return;
    cmpEvaluationFrame = windowObject.requestAnimationFrame(() => {
      cmpEvaluationFrame = null;
      evaluateCmp();
    });
  };

  const attachCmpRoot = root => {
    cmpRoot = root;
    rootDiscoveryObserver?.disconnect();
    rootDiscoveryObserver = null;
    if (discoveryTimeout !== null) windowObject.clearTimeout(discoveryTimeout);
    cmpDiscoveryExpired = false;
    discoveryTimeout = windowObject.setTimeout(() => {
      discoveryTimeout = null;
      cmpDiscoveryExpired = true;
      evaluateCmp();
    }, CMP_DISCOVERY_TIMEOUT_MS);
    rootObserver = new windowObject.MutationObserver(queueCmpEvaluation);
    rootObserver.observe(root, {
      attributes: true,
      attributeFilter: ['class', 'hidden', 'style', 'aria-hidden'],
      childList: true,
      subtree: true,
    });
    evaluateCmp();
  };

  const existingRoot = documentObject.querySelector(CMP_ROOT_SELECTOR);
  if (existingRoot) {
    attachCmpRoot(existingRoot);
  } else {
    rootDiscoveryObserver = new windowObject.MutationObserver(() => {
      const discoveredRoot = documentObject.querySelector(CMP_ROOT_SELECTOR);
      if (discoveredRoot) attachCmpRoot(discoveredRoot);
    });
    rootDiscoveryObserver.observe(documentObject.body, {
      childList: true,
      subtree: true,
    });
    discoveryTimeout = windowObject.setTimeout(() => {
      rootDiscoveryObserver?.disconnect();
      rootDiscoveryObserver = null;
      // CMP unavailable is a fail-closed state: no SDK is injected.
      machine.update(true);
    }, CMP_DISCOVERY_TIMEOUT_MS);
  }

  return {
    destroy() {
      machine.cancel();
      rootObserver?.disconnect();
      rootDiscoveryObserver?.disconnect();
      stopWatchingForChatHost();
      if (discoveryTimeout !== null)
        windowObject.clearTimeout(discoveryTimeout);
      if (consentRetryTimeout !== null)
        windowObject.clearTimeout(consentRetryTimeout);
      if (cmpEvaluationFrame !== null)
        windowObject.cancelAnimationFrame(cmpEvaluationFrame);
      chatHosts.forEach(teardownCorporateChatExperience);
    },
    getState: machine.getState,
  };
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  startPicklesChatLoader();
}
